import { useCallback, useMemo } from 'react';
import styled from 'styled-components';

import { t } from '@lingui/macro';

import toColorString from 'polished/lib/color/toColorString';

import color from '@decisiv/design-tokens/lib/color';

import Times from '@decisiv/iconix/lib/components/Times';
import InfoCircleF from '@decisiv/iconix/lib/components/InfoCircleF';

import Button from '@decisiv/ui-components/lib/components/Button';
import Flex from '@decisiv/ui-components/lib/components/Flex';
import Loading from '@decisiv/ui-components/lib/components/Loading';
import { P } from '@decisiv/ui-components/lib/components/Typography';

import useApiUserValidQuery from 'pages/Teams/hooks/useApiUserValidQuery';

import {
  generateUserValidationError,
  generatePlatformAccountName,
  UserValidationError,
} from 'utils/SentinelTeam';

import {
  useManagementTeamModalContext,
  TeamSource,
} from '../contexts/ManagementTeamModalContext';

const Container = styled(Flex)<{ valid: boolean }>`
  align-items: center;
  justify-content: space-between;

  background: ${({ valid }) =>
    /* istanbul ignore next -- @preserve */
    valid
      ? toColorString(color.base.fullMoon)
      : toColorString(color.status.warning.light)};
`;

export default function ManagementSourceItem({
  source,
}: {
  source: TeamSource;
}) {
  const { values, setValues } = useManagementTeamModalContext();

  const { dealerName, sourceType, value, valid, validated, errors } = source;
  const { username, password, ownerEmail } = values;

  // teamType is always FLEET in the management variant
  const teamType = 'FLEET' as const;

  /* istanbul ignore next -- @preserve */
  const handleValidationCompleted = useCallback(
    (data) => {
      if (!data) return;

      const { apiUserValid, userEmailValid } = data;
      if (!apiUserValid || !userEmailValid) return;

      const { valid: apiValid, errors: apiErrors } = apiUserValid;
      const { valid: emailValid } = userEmailValid;

      setValues((oldState) => ({
        ...oldState,
        sourceIds: oldState.sourceIds.map((src) =>
          src.value === value
            ? {
                ...src,
                valid: apiValid,
                validated: true,
                errors: apiErrors,
                emailValid,
              }
            : src,
        ),
      }));
    },
    [setValues, value],
  );

  const shouldSkip = useMemo(
    () => validated || !username || !password || !ownerEmail,
    [password, username, validated, ownerEmail],
  );

  /* istanbul ignore next -- @preserve */
  const { loading } = useApiUserValidQuery({
    variables: {
      username,
      password,
      email: ownerEmail,
      teamType,
      sourceId: value,
      sourceType,
    },
    fetchPolicy: 'network-only',
    onCompleted: handleValidationCompleted,
    skip: shouldSkip,
    onError: () => {
      const error = 'INTERNAL_ERROR';

      setValues((oldState) => ({
        ...oldState,
        sourceIds: oldState.sourceIds.map((src) =>
          src.value === value
            ? { ...src, valid: false, validated: true, errors: [error] }
            : src,
        ),
      }));
    },
  });

  const validationError = useMemo(
    () =>
      generateUserValidationError(
        errors?.[0] as UserValidationError,
        sourceType,
        value,
      ) || t`Internal Error`,
    [errors, sourceType, value],
  );

  const isInvalid = useMemo(() => !valid && validated, [valid, validated]);

  const handleRemoveItem = useCallback(() => {
    setValues((oldState) => ({
      ...oldState,
      sourceIds: [...oldState.sourceIds.filter((i) => i.value !== value)],
    }));
  }, [setValues, value]);

  const label = dealerName || generatePlatformAccountName(sourceType, value);

  const loadingIndicator =
    /* istanbul ignore next -- @preserve */
    loading ? (
      <Loading marginRight={0.8} size="small" title={t`Validating...`} />
    ) : null;

  const showErrors = errors.length > 0 && !loading;
  const errorDisplay = showErrors ? (
    <>
      <InfoCircleF
        size="medium"
        color={toColorString(color.status.warning.medium)}
      />
      <P color="alaskanHusky" size="small" marginLeft={0.5}>
        {validationError}
      </P>
    </>
  ) : null;

  return (
    <Container paddingX={1} paddingY={1.1} valid={!isInvalid}>
      <Flex alignItems="center">
        {loadingIndicator}
        <P marginRight={0.8}>{label}</P>
        {errorDisplay}
      </Flex>
      <Button
        icon={Times}
        variant="ghost"
        onClick={handleRemoveItem}
        aria-label={t`Remove ${value}`}
      />
    </Container>
  );
}
